import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ChevronLeft, ChevronRight, LayoutGrid, List, Search } from 'lucide-react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';

const PAGE_SIZE_OPTIONS = [20, 50, 100];
import { DeviceTable } from '../components/DeviceTable';
import { DeviceGrid } from '../components/DeviceGrid';
import { PushToMyDevicesDialog } from '../components/PushToMyDevicesDialog';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { FacetCombobox } from '../components/FacetCombobox';
import { FacetSuggestions } from '../components/FacetSuggestions';
import { matchingFacets, toggleFacetValue, type FacetValue } from '../utils/device-facets';
import { useStaffCustomerDevices, type MdmDevice } from '../hooks/useStaffCustomerDevices';
import { useStaffGetCustomer } from '@/features/customers/hooks/useStaffCustomer';
import { CustomerDeviceDrawer } from '@/features/agent-devices/components/testing-drawer/TestingDeviceDrawer';
import { useDeviceHeartbeats } from '../hooks/useDeviceHeartbeats';
import { useMdmDevicesFilterStore } from '../stores/mdm-devices-filter-store';
import { useMdmDevicesSelectionStore } from '../stores/mdm-devices-selection-store';
import { useMdmDevicesViewStore } from '../stores/mdm-devices-view-store';

/**
 * Staff variant of the MDM DevicesPage. Renders the same table / grid /
 * drawer / column customizer as `apps/web/features/mdm-devices/pages/DevicesPage`
 * but bound to `staffCustomerDevices(accountId)` instead of the team-scoped
 * `devices()` query.
 *
 * Deltas vs apps/web copy (keep in sync w/ staff-devices-sync.md):
 *  - Accepts `accountId` prop rather than relying on the signed-in user's team.
 *  - Drops `BulkActionBar` + `MoveToFleetDialog` + Fleet nav button — staff is
 *    read-only across customers in Phase 1.
 *  - The drawer is the My Devices drawer in customer mode
 *    (`CustomerDeviceDrawer`), not the scio3 copy.
 *  - Drops `FleetFilterDropdown` — fleet filter on a specific customer is
 *    usually unhelpful. If support asks for it later, lift it from the web
 *    copy on the next sync pass.
 *  - Deep-link param (`?device=`) still honored so copy-pasting a URL still
 *    opens the drawer for that UUID / _id.
 */
interface Props {
  accountId: string | undefined;
}

export function DevicesPage({ accountId }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();

  const searchText = useMdmDevicesFilterStore((s) => s.searchText);
  const setSearchText = useMdmDevicesFilterStore((s) => s.setSearchText);
  const teamIds = useMdmDevicesFilterStore((s) => s.teamIds);
  const setTeamIds = useMdmDevicesFilterStore((s) => s.setTeamIds);
  const folderPaths = useMdmDevicesFilterStore((s) => s.folderPaths);
  const setFolderPaths = useMdmDevicesFilterStore((s) => s.setFolderPaths);
  const clearFilters = useMdmDevicesFilterStore((s) => s.clear);
  const hasActiveFilters = useMdmDevicesFilterStore((s) => s.hasActive());

  const viewMode = useMdmDevicesViewStore((s) => s.viewMode);
  const setViewMode = useMdmDevicesViewStore((s) => s.setViewMode);
  const setColumnVisibility = useMdmDevicesViewStore((s) => s.setColumnVisibility);

  const clearSelection = useMdmDevicesSelectionStore((s) => s.clear);

  const { devices, teamOptions, folderOptions, loading, totalCount, error } =
    useStaffCustomerDevices({ accountId });

  // Filtering by a facet reveals its column — otherwise you narrow to a folder
  // and the table still can't show you which folder each row is in.
  const handleTeamChange = (next: FacetValue[]) => {
    setTeamIds(next);
    if (next.length > 0) setColumnVisibility('team', true);
  };
  const handleFolderChange = (next: FacetValue[]) => {
    setFolderPaths(next);
    if (next.length > 0) setColumnVisibility('path', true);
  };

  // The search box matches the screen itself (name / UUID / serial), so a query
  // that is really a site name finds nothing on a folder-organised account.
  // Offer the facet it meant instead.
  const suggestions = useMemo(
    () => matchingFacets(searchText, teamOptions, folderOptions),
    [searchText, teamOptions, folderOptions],
  );
  const hasSuggestions = suggestions.teams.length > 0 || suggestions.folders.length > 0;

  /** Picking a suggestion clears the query — it named a folder, not a device. */
  const applySuggestedTeam = (value: FacetValue) => {
    setSearchText('');
    handleTeamChange(toggleFacetValue(teamIds, value));
  };
  const applySuggestedFolder = (value: FacetValue) => {
    setSearchText('');
    handleFolderChange(toggleFacetValue(folderPaths, value));
  };
  // Live online/HDMI/offline status from open-api's realtime heartbeat — drives
  // the per-row status dots (more current than the stored-lastHeartBeat rollup).
  const { statusByUuid } = useDeviceHeartbeats((devices as MdmDevice[]).map((d) => d.UUID));
  // Summary counts come from the backend customer rollup (same source as the
  // dashboard cards in CustomerShell) so the two always match and cover ALL
  // devices, not just the loaded page. Apollo-cached — no extra fetch.
  const customer = useStaffGetCustomer(accountId).data?.staffGetCustomer;

  const [drawerDeviceId, setDrawerDeviceId] = useState<string | null>(null);
  const [pushContentId, setPushContentId] = useState<string | null>(null);
  const handledDeviceParamRef = useRef<string | null>(null);

  // Client-side pagination over the full (filtered + sorted) device list.
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  // Reset to the first page whenever the filtered set changes (search/filters).
  useEffect(() => {
    setPage(0);
  }, [searchText, teamIds, folderPaths, pageSize]);

  const filteredTotal = devices.length;
  const pageCount = Math.max(1, Math.ceil(filteredTotal / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const pageStart = currentPage * pageSize;
  const pagedDevices = devices.slice(pageStart, pageStart + pageSize);

  useEffect(() => {
    const param = searchParams.get('device') ?? searchParams.get('deviceUUID');
    if (!param || handledDeviceParamRef.current === param) return;
    handledDeviceParamRef.current = param;
    setDrawerDeviceId(param);
  }, [searchParams]);

  const handleRowClick = (deviceId: string) => {
    setDrawerDeviceId(deviceId);
    const params = new URLSearchParams(searchParams);
    params.set('device', deviceId);
    setSearchParams(params, { replace: true });
  };

  const handleDrawerClose = () => {
    setDrawerDeviceId(null);
    const params = new URLSearchParams(searchParams);
    params.delete('device');
    params.delete('deviceUUID');
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    clearSelection();
    return () => clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  if (error) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        {error.message}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="mdm-devices-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground" data-testid="mdm-devices-summary">
            {`${customer?.totalDevices ?? totalCount ?? devices.length} devices · ${customer?.onlineDevices ?? 0} online · ${customer?.staleDevices ?? 0} stale · ${customer?.offlineDevices ?? 0} offline`}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="mdm-devices-toolbar">
        <div className="relative min-w-[310px] flex-1">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={'Name, UUID, serial, team or folder'}
            className="pl-8"
            aria-label={'Search devices by name, UUID, serial, team or folder'}
            data-testid="mdm-devices-search"
          />
        </div>
        {/* Team and folder are deliberately two pickers, not one blended search:
            a screen's owning team and the folder it sits in are independent, and
            on folder-organised accounts every team can read 0 while the folders
            hold everything. See utils/device-facets.ts. */}
        <FacetCombobox
          label="Team"
          allLabel="All teams"
          options={teamOptions}
          pluralLabel="teams"
          value={teamIds}
          onChange={handleTeamChange}
          testId="mdm-devices-team-filter"
        />
        <FacetCombobox
          label="Folder"
          allLabel="All folders"
          options={folderOptions}
          pluralLabel="folders"
          value={folderPaths}
          onChange={handleFolderChange}
          testId="mdm-devices-folder-filter"
        />
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="cursor-pointer"
            data-testid="mdm-devices-clear-filters"
          >
            {'Reset'}
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <ColumnCustomizer />
          <div className="inline-flex rounded-md border p-0.5">
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="icon-sm"
              aria-label={'List view'}
              onClick={() => setViewMode('list')}
              className="cursor-pointer"
              data-testid="mdm-devices-view-list"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              size="icon-sm"
              aria-label={'Grid view'}
              onClick={() => setViewMode('grid')}
              className="cursor-pointer"
              data-testid="mdm-devices-view-grid"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <FacetSuggestions
        query={searchText}
        teams={suggestions.teams}
        folders={suggestions.folders}
        teamTotal={suggestions.teamTotal}
        folderTotal={suggestions.folderTotal}
        onPickTeam={applySuggestedTeam}
        onPickFolder={applySuggestedFolder}
      />

      {loading && devices.length === 0 ? (
        <div className="rounded-lg border bg-card p-6 text-center text-sm text-muted-foreground shadow-sm">
          {'Loading...'}
        </div>
      ) : devices.length === 0 ? (
        <div
          className="rounded-lg border bg-card p-12 text-center text-sm text-muted-foreground shadow-sm"
          data-testid="mdm-devices-empty"
        >
          <p>{searchText.trim() ? `No screens match "${searchText.trim()}"` : 'No devices'}</p>
          {/* Team and folder ARE reachable from the search box — a name match
              surfaces as a chip above. So the hint claims all five dimensions,
              and only the closing half changes: point at the chip when there is
              one, otherwise say plainly that nothing matched anywhere. */}
          {searchText.trim() ? (
            <p className="mx-auto mt-2 max-w-md" data-testid="mdm-devices-empty-search-hint">
              {hasSuggestions
                ? 'Search covers the screen name, UUID and serial, plus team and folder — pick the match above to filter by it.'
                : 'Search covers the screen name, UUID and serial, plus team and folder names. Nothing matched any of them.'}
            </p>
          ) : null}
          {/* The common false alarm: a team owns nothing because the account
              files screens in folders and shares those folders with the team.
              Point support at the folder picker instead of a support ticket. */}
          {teamIds.length > 0 && folderPaths.length === 0 ? (
            <p className="mx-auto mt-2 max-w-md" data-testid="mdm-devices-empty-team-hint">
              {
                'No screens are owned by the selected team(s). Screens a team can see may be owned by another team and shared with it through a folder — try the Folder filter instead.'
              }
            </p>
          ) : null}
        </div>
      ) : (
        <>
          {/* Pagination — page size selector + prev/next over the full set.
              Sits between the search toolbar and the device list. */}
          <div
            className="flex flex-wrap items-center justify-between gap-3"
            data-testid="mdm-devices-pagination"
          >
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{'Rows per page'}</span>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => setPageSize(Number(v))}
              >
                <SelectTrigger
                  className="h-8 w-[72px] cursor-pointer"
                  aria-label={'Rows per page'}
                  data-testid="mdm-devices-page-size"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)} className="cursor-pointer">
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span data-testid="mdm-devices-page-range">
                {filteredTotal === 0
                  ? '0 of 0'
                  : `${pageStart + 1}–${Math.min(pageStart + pageSize, filteredTotal)} of ${filteredTotal}`}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon-sm"
                  aria-label={'Previous page'}
                  disabled={currentPage <= 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="cursor-pointer"
                  data-testid="mdm-devices-prev-page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="min-w-[5.5rem] text-center">
                  {`Page ${currentPage + 1} of ${pageCount}`}
                </span>
                <Button
                  variant="outline"
                  size="icon-sm"
                  aria-label={'Next page'}
                  disabled={currentPage >= pageCount - 1}
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  className="cursor-pointer"
                  data-testid="mdm-devices-next-page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {viewMode === 'grid' ? (
            <DeviceGrid
              devices={pagedDevices}
              onCardClick={handleRowClick}
              liveStatusByUuid={statusByUuid}
            />
          ) : (
            <DeviceTable
              devices={pagedDevices}
              onRowClick={handleRowClick}
              onAssignClick={setPushContentId}
              liveStatusByUuid={statusByUuid}
            />
          )}
        </>
      )}

      {/* The My Devices drawer in customer mode — its accountId-taking ops are gated to this account. */}
      {accountId ? (
        <CustomerDeviceDrawer
          accountId={accountId}
          companyName={customer?.companyName}
          devices={devices}
          deviceId={drawerDeviceId}
          onClose={handleDrawerClose}
          onAssignClick={setPushContentId}
          liveStatusByUuid={statusByUuid}
        />
      ) : null}

      <PushToMyDevicesDialog
        contentId={pushContentId}
        onOpenChange={(o) => !o && setPushContentId(null)}
      />
    </div>
  );
}
